<script setup lang="ts">
// TODO :: TASK-2103 - Should be promoted to a organism
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { storeToRefs } from 'pinia';
import InputNumber from 'primevue/inputnumber';
import { applyShelterCurrency, toCents } from '@Helpers';
import { useAToast } from '@Atoms';
import type { MedicalCost } from '../../../interfaces';
import { MEDICAL_COST_TYPES } from '../../../enums';
import { UpdateMedicalCostOnCartService, UpdateMedicalCostRetailCostService } from '../../../services';
import { shouldShowMarkdownRetailComparison } from '../../../helpers';
import useShoppingCartStore from '../../../store';

interface MedicalCostRow extends MedicalCost {
  categoryLabel?: string;
}

const props = withDefaults(
  defineProps<{
    allowReceiptInclusionToggle?: boolean;
    canEditRetailCost?: boolean;
    cost: MedicalCostRow;
    isEditing: boolean;
  }>(),
  {
    allowReceiptInclusionToggle: true,
    canEditRetailCost: false,
  }
);

const showRetailCostEdit = computed(() => props.isEditing && props.canEditRetailCost);

const { cartData } = storeToRefs(useShoppingCartStore());
const { locale } = useI18n();
const { notify, TOAST_TYPES } = useAToast();

const TOAST_UPDATE_ERROR = {
  message: 'cart.toast.addItem.error.message',
  title: 'errors.generics.unknownIssue.title',
  type: TOAST_TYPES.ERROR,
};

const isCostEditOpen = ref(false);
const retailCostInput = ref(0);
const loading = ref(false);

const inputLocale = computed(() => (locale.value === 'pt-BR' ? 'pt-BR' : 'en-US'));

const displayRetailCost = computed(() => {
  const cost = Number(props.cost.retail_cost ?? 0) || 0;

  if (isVaccinationWithDoseCount.value) {
    const doseCount = Number(props.cost.dose_count) || 1;
    return cost * doseCount;
  }

  return cost;
});

const isOnReceipt = computed(() => props.cost.add_to_receipt ?? props.cost.add_to_total ?? false);

const isVaccinationWithDoseCount = computed(
  () =>
    props.cost.type === MEDICAL_COST_TYPES.VACCINATIONS &&
    props.cost.dose_count != null &&
    Number(props.cost.dose_count) > 0
);

const originalRetailCostDisplay = computed(() => {
  const cost = props.cost.original_retail_cost ?? props.cost.medical_costs_settings?.original_retail_cost;

  if (cost === undefined || cost === null) return null;

  return typeof cost === 'number' ? cost : parseFloat(String(cost)) || 0;
});

const showMarkdownPriceComparison = computed(() =>
  originalRetailCostDisplay.value == null
    ? false
    : shouldShowMarkdownRetailComparison(originalRetailCostDisplay.value, displayRetailCost.value)
);

watch(
  () => displayRetailCost.value,
  (val) => (retailCostInput.value = val),
  { immediate: true }
);

watch(
  () => props.canEditRetailCost,
  (allowed) => !allowed && (isCostEditOpen.value = false)
);

// EVENTS
function openCostEdit() {
  isCostEditOpen.value = true;
  retailCostInput.value = displayRetailCost.value;
}

function onCloseCostEdit() {
  isCostEditOpen.value = false;
}

function onConfirmRetailCost() {
  let retailCostToSend =
    typeof retailCostInput.value === 'number' ? retailCostInput.value : parseFloat(String(retailCostInput.value)) || 0;

  if (isVaccinationWithDoseCount.value) {
    const doseCount = Number(props.cost.dose_count) || 1;

    retailCostToSend = toCents(retailCostToSend) / doseCount / 100;
  }

  loading.value = true;

  const PAYLOAD = {
    _id: props.cost._id,
    animal_id: props.cost.animal_id,
    type: props.cost.type,
    retail_cost: retailCostToSend,
  };

  UpdateMedicalCostRetailCostService(PAYLOAD)
    .then((data) => {
      cartData.value = data;
      onCloseCostEdit();
    })
    .catch(() => notify(TOAST_UPDATE_ERROR))
    .finally(() => (loading.value = false));
}

function onAddToReceiptChange(value: boolean) {
  const PAYLOAD = {
    _id: props.cost._id,
    animal_id: props.cost.animal_id,
    type: props.cost.type,
    add_to_receipt: value,
  };

  loading.value = true;

  UpdateMedicalCostOnCartService(PAYLOAD)
    .then(({ data }) => (cartData.value = data))
    .catch(() => notify(TOAST_UPDATE_ERROR))
    .finally(() => (loading.value = false));
}
</script>

<template>
  <div class="flex flex-col items-start justify-between gap-2 py-1 sm:flex-row sm:items-center">
    <span :class="['min-w-0 flex-1 truncate', isOnReceipt ? '' : 'line-through opacity-50']">
      {{ cost.item || $t('cart.summary.medicalItem') }}
    </span>

    <div class="flex items-center gap-1">
      <template v-if="showRetailCostEdit">
        <div
          v-if="isCostEditOpen"
          class="flex shrink-0 items-center gap-1"
          @keydown.enter.prevent="onConfirmRetailCost"
          @keydown.esc="onCloseCostEdit"
        >
          <InputNumber
            v-model="retailCostInput"
            currency="USD"
            mode="currency"
            :locale="inputLocale"
            :minFractionDigits="2"
            :maxFractionDigits="2"
            :disabled="loading"
            pt:input:root="text-sm h-9 min-w-24 w-28 text-center"
          />

          <AShoppingCartIconButton
            compact
            icon="pi pi-check"
            :ariaLabel="$t('cart.summary.ariaConfirmAmount')"
            :disabled="loading"
            customClass="text-brand-500"
            @onClick="onConfirmRetailCost"
          />

          <AShoppingCartIconButton
            compact
            icon="pi pi-times"
            :ariaLabel="$t('cart.summary.ariaCancelEdit')"
            :disabled="loading"
            customClass="text-red-600"
            @onClick="onCloseCostEdit"
          />
        </div>

        <template v-else>
          <AShoppingCartAmountDisplay
            :amount="applyShelterCurrency(displayRetailCost)"
            :hasDiscount="showMarkdownPriceComparison"
            :originalAmount="
              showMarkdownPriceComparison && originalRetailCostDisplay != null
                ? applyShelterCurrency(originalRetailCostDisplay)
                : null
            "
            :wrapperClass="
              isOnReceipt
                ? 'flex shrink-0 flex-col items-end text-right'
                : 'flex shrink-0 flex-col items-end text-right line-through opacity-50'
            "
          />

          <AShoppingCartIconButton
            compact
            icon="pi pi-pencil"
            :ariaLabel="$t('cart.summary.ariaEditAmount')"
            :disabled="loading"
            @onClick="openCostEdit"
          />
        </template>
      </template>

      <AShoppingCartAmountDisplay
        v-else
        :amount="applyShelterCurrency(displayRetailCost)"
        :hasDiscount="showMarkdownPriceComparison"
        :originalAmount="
          showMarkdownPriceComparison && originalRetailCostDisplay != null
            ? applyShelterCurrency(originalRetailCostDisplay)
            : null
        "
        :wrapperClass="
          isOnReceipt
            ? 'flex shrink-0 flex-col items-end text-right'
            : 'flex shrink-0 flex-col items-end text-right line-through opacity-50'
        "
      />

      <Checkbox
        v-if="allowReceiptInclusionToggle"
        binary
        :disabled="loading"
        :modelValue="isOnReceipt"
        @update:model-value="onAddToReceiptChange"
      />
    </div>
  </div>
</template>
